import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

const ConfirmDialogContext = createContext(null);

const DEFAULT_OPTIONS = {
  title: "CONFIRM ACTION",
  message: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  hideCancel: false,
  cancelFirst: false,
  consentLabel: null,
  requireConsent: false,
};

export function ConfirmDialogProvider({ children }) {
  const [dialogOptions, setDialogOptions] = useState(DEFAULT_OPTIONS);
  const [isOpen, setIsOpen] = useState(false);
  const resolveRef = useRef(null);

  const closeDialog = useCallback((result) => {
    setIsOpen(false);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  const openDialog = useCallback((options = {}) => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }

    setDialogOptions({
      ...DEFAULT_OPTIONS,
      ...options,
    });
    setIsOpen(true);

    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const confirm = useCallback(
    (options = {}) =>
      openDialog({
        ...options,
        hideCancel: false,
      }),
    [openDialog]
  );

  const alertDialog = useCallback(
    (options = {}) =>
      openDialog({
        confirmText: "OK",
        ...options,
        hideCancel: true,
      }),
    [openDialog]
  );

  const contextValue = useMemo(
    () => ({
      confirm,
      alert: alertDialog,
    }),
    [confirm, alertDialog]
  );

  useEffect(() => {
    return () => {
      if (resolveRef.current) {
        resolveRef.current(false);
        resolveRef.current = null;
      }
    };
  }, []);

  return (
    <ConfirmDialogContext.Provider value={contextValue}>
      {children}
      <ConfirmDialog
        isOpen={isOpen}
        title={dialogOptions.title}
        message={dialogOptions.message}
        confirmText={dialogOptions.confirmText}
        cancelText={dialogOptions.cancelText}
        hideCancel={dialogOptions.hideCancel}
        cancelFirst={dialogOptions.cancelFirst}
        consentLabel={dialogOptions.consentLabel}
        requireConsent={dialogOptions.requireConsent}
        onConfirm={() => closeDialog(true)}
        onCancel={() => closeDialog(false)}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error("useConfirmDialog must be used within ConfirmDialogProvider.");
  }
  return context.confirm;
}

export function useAlertDialog() {
  const context = useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error("useAlertDialog must be used within ConfirmDialogProvider.");
  }
  return context.alert;
}
